import styled from 'styled-components/macro'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'
import { Layout, Text } from '@loadsmart/miranda-react'

import { type Stop, getDelivery, getExtraStops, getPickup, isMultiStop } from 'core/domain/Stop'
import { formatLaneDistance } from 'core/presenters/stopPresenter'
import { ExtraStopInformation } from 'core/ui/components/stop/ExtraStopInformation'

import { screen } from 'legacy/styles/screen'

import { type DistanceUnit } from 'rfp/domain/DistanceUnit'

import { Information } from './Information'

const Divider = styled.div`
  background: ${getToken('color-neutral-light')};
  height: ${getToken('space-2xs')};
  width: ${getToken('space-xl')};
  display: none;

  ${screen.md} {
    display: block;
  }
`

const LaneWrapper = styled(Layout.Stack)`
  gap: ${getToken('space-m')};

  ${screen.md} {
    align-items: center;
    flex-direction: row;
  }
`

export type StopInformationProps = {
  distance?: number
  stops: Array<Stop>
  locationFormatter: (stop: Stop) => string
  dateFormatter: (stop: Stop) => string | undefined
  distanceUnit: DistanceUnit
}

export function StopInformation({
  distance,
  stops,
  locationFormatter,
  dateFormatter,
  distanceUnit,
  ...props
}: StopInformationProps) {
  if (stops.length < 2) {
    return null
  }

  const hasDistance = Boolean(distance)
  const hasExtraStops = isMultiStop(stops)
  const shouldDisplayExtraInformation = hasDistance || hasExtraStops

  const pickup = getPickup(stops)
  const delivery = getDelivery(stops)

  return (
    <LaneWrapper align="flex-start" justify="flex-start" {...props}>
      <Information
        locationInfo={locationFormatter(pickup)}
        dateInfo={dateFormatter(pickup) ?? ''}
        facilityName={pickup.facilityName}
      />
      <Divider />
      {shouldDisplayExtraInformation && (
        <>
          <Layout.Stack align="center" gap="spacing-1">
            {hasDistance && (
              <Text variant="body-md-bold">{`${formatLaneDistance(distance, distanceUnit)}`}</Text>
            )}
            {hasExtraStops && (
              <ExtraStopInformation
                stops={getExtraStops(stops)}
                titleFormatter={locationFormatter}
                subTitleFormatter={dateFormatter}
              />
            )}
          </Layout.Stack>
          <Divider />
        </>
      )}
      <Information
        locationInfo={locationFormatter(delivery)}
        dateInfo={dateFormatter(delivery) ?? ''}
        facilityName={delivery.facilityName}
      />
    </LaneWrapper>
  )
}
